/* cart-dependency-logic.js
 *
 * hey-listen.ai - Auto-add + replace dependency items for subscription products
 * 
 * Funktionalität:
 * - Wenn Abo im Warenkorb: automatisch Basic Onboarding + Wartung & Service Basicspaket hinzufügen
 * - Wenn Premium Onboarding/Wartung hinzugefügt: entsprechendes Basic entfernen (Replace-Logik)
 * - Keine Duplikate zulassen (Basic + Premium gleichzeitig)
 * - Guard + Debounce gegen Endlosschleifen und Race Conditions
 * - Selling Plans am Abo bleiben unangetastet
 */

(() => {
  // ========================================
  // KONFIGURATION - HIER ANPASSEN
  // ========================================
  const CONFIG = {
    // Debug-Modus: true = Console-Logs anzeigen, false = stumm
    debug: false,

    // Abo-Erkennung: Nutze explizite Product-IDs (alle Plus, Pro, Essential Abos)
    useProductType: false,
    aboProductTypeValue: "Abo",
    
    // Alle Abo-Product-IDs (Plus, Pro, Essential)
    aboProductIds: [
      15436823101788,  // E-Commerce Abo Plus #1
      15436823331164,  // E-Commerce Abo Plus #2
      15436824215900,  // Hausverwaltung Abo - Plus #1
      15436824445276,  // Hausverwaltung Abo - Plus #2
      15436822413660,  // Pflegeheim Abo Plus #2
      15436822151516,  // Pflegeheim Plus Abo #2
      15433883746652,  // Restaurant Abo Plus #1
      15436815040860,  // Restaurant Plus Abo #2
      15436823560540,  // E-Commerce Abo Pro
      15436824609116,  // Hausverwaltung Abo Pro
      15436822610268,  // Pflegeheim Abo Pro
      15436815237468,  // Restaurant Abo Pro
      15436821758300,  // Pflegeheim Essential Abo
      15433855861084,  // Restaurant Essential Abo
      15525547835740,  // Restaurant Plus Abo – Takeaway via HubRise
      15525547966812,  // Restaurant Abo Plus – Reservierungen via Resos
      15436821463388,  // PIA – Persönliche Telefonassistenz
      15436822839644,  // E-Commerce Abo Essential
      15436823888220,  // Hausverwaltung Abo Essential
    ],

    // Variant-IDs für Onboarding und Wartungspaket
    variants: {
      onboarding: {
        basic: 56701024862556,      // Basic Onboarding
        premium: 56718294024540,    // Premium Onboarding
      },
      wartung: {
        basic: 56721989501276,      // Wartung & Service Basicspaket
        premium: 56722099044700,    // Wartung & Service Advancedspaket
      },
    },

    // Mengen beim Hinzufügen
    qty: {
      onboarding: 1,
      wartung: 1,
    },

    // Debounce-Verzögerung (ms) - verhindert zu häufiges Ausführen
    debounceMs: 250,
    
    // Maximale Operationen pro Durchlauf (Sicherheit)
    maxOpsPerRun: 6,
  };

  // ========================================
  // INTERNE VARIABLEN (Guard + Debounce)
  // ========================================
  let isRunning = false;          // Verhindert parallele Ausführung
  let queued = false;             // Merkt sich, ob nochmal laufen soll
  let debounceTimer = null;       // Verzögerungs-Timer
  let ownCartCall = false;        // Verhindert Endlosschleife im Fetch-Interceptor

  // ========================================
  // LOGGING
  // ========================================
  const log = (...args) => {
    if (CONFIG.debug) {
      console.log("[CartDependencyLogic]", ...args);
    }
  };

  // ========================================
  // API-AUFRUFE
  // ========================================
  
  /**
   * Fetch mit JSON-Antwort
   */
  async function fetchJSON(url) {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} for ${url}`);
    }
    return res.json();
  }

  /**
   * POST mit JSON-Body und JSON-Antwort
   */
  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(`Non-JSON response from ${url}: ${text.slice(0, 120)}...`);
    }
  }

  /**
   * Hole aktuellen Warenkorb
   */
  async function getCart() {
    return fetchJSON("/cart.js");
  }

  // ========================================
  // WARENKORB-PRÜFUNG
  // ========================================

  /**
   * Prüfe, ob ein Abo-Produkt im Warenkorb ist
   */
  function hasAbo(cart) {
    if (!cart?.items?.length) return false;

    // Prüfe explizite Product-IDs
    const aboSet = new Set(CONFIG.aboProductIds);
    return cart.items.some((item) => aboSet.has(item.product_id));
  }

  /**
   * Finde alle Line Items einer bestimmten Variant
   */
  function findLinesByVariant(cart, variantId) {
    return cart.items.filter((item) => item.variant_id === variantId);
  }

  /**
   * Prüfe, ob eine Variant im Warenkorb existiert
   */
  function hasVariant(cart, variantId) {
    return findLinesByVariant(cart, variantId).length > 0;
  }

  // ========================================
  // WARENKORB-ÄNDERUNGEN
  // ========================================

  /**
   * Entferne alle Line Items einer Variant (Schutz vor Duplikaten)
   */
  async function removeAllVariant(cart, variantId) {
    const lines = findLinesByVariant(cart, variantId);
    for (const line of lines) {
      ownCartCall = true;
      try {
        await postJSON("/cart/change.js", {
          id: line.key,
          quantity: 0,
        });
        log(`✓ Entfernt: Variant ${variantId} (Line Key: ${line.key})`);
      } finally {
        ownCartCall = false;
      }
    }
  }

  /**
   * Füge eine Variant zum Warenkorb hinzu
   */
  async function addVariant(variantId, quantity = 1) {
    ownCartCall = true;
    try {
      await postJSON("/cart/add.js", {
        id: variantId,
        quantity: quantity,
      });
      log(`✓ Hinzugefügt: Variant ${variantId} (Menge: ${quantity})`);
    } finally {
      ownCartCall = false;
    }
  }

  // ========================================
  // DEBOUNCE + SCHEDULING
  // ========================================

  /**
   * Plane einen Durchlauf mit Verzögerung
   */
  function scheduleRun(reason) {
    if (debounceTimer) clearTimeout(debounceTimer);
    
    debounceTimer = setTimeout(() => {
      run(reason).catch((e) => {
        log("❌ Fehler während Durchlauf:", e);
      });
    }, CONFIG.debounceMs);
  }

  // ========================================
  // HAUPTLOGIK
  // ========================================

  /**
   * Führe die Abhängigkeits-Logik aus
   */
  async function run(reason) {
    // Guard: Wenn bereits am Laufen, in Queue einreihen
    if (isRunning) {
      queued = true;
      log(`⏳ Durchlauf in Queue (bereits am Laufen). Grund: ${reason}`);
      return;
    }

    isRunning = true;
    queued = false;

    try {
      log(`\n🔄 Durchlauf gestartet. Grund: ${reason}`);

      let operations = 0;
      const cart = await getCart();

      // Schritt 1: Prüfe, ob Abo im Warenkorb
      if (!hasAbo(cart)) {
        log("ℹ️  Kein Abo im Warenkorb → keine Aktion nötig.");
        return;
      }

      log("✓ Abo im Warenkorb erkannt");

      // Konfiguration auslesen
      const ob = CONFIG.variants.onboarding;
      const wa = CONFIG.variants.wartung;

      // Schritt 2: Aktuellen Zustand prüfen
      const hasObBasic = hasVariant(cart, ob.basic);
      const hasObPremium = hasVariant(cart, ob.premium);
      const hasWaBasic = hasVariant(cart, wa.basic);
      const hasWaPremium = hasVariant(cart, wa.premium);

      log(`Zustand: Onboarding(Basic:${hasObBasic}, Premium:${hasObPremium}), Wartung(Basic:${hasWaBasic}, Premium:${hasWaPremium})`);

      // Schritt 3: Erzwinge Exklusivität (Premium gewinnt)
      // Wenn Premium und Basic beide existieren → entferne Basic
      if (hasObPremium && hasObBasic) {
        log("⚠️  Premium Onboarding + Basic Onboarding gefunden → entferne Basic");
        await removeAllVariant(cart, ob.basic);
        operations++;
      }

      if (hasWaPremium && hasWaBasic) {
        log("⚠️  Wartung & Service Advanced + Wartung & Service Basic gefunden → entferne Basic");
        await removeAllVariant(cart, wa.basic);
        operations++;
      }

      // Sicherheit: Nicht zu viele Operationen pro Durchlauf
      if (operations >= CONFIG.maxOpsPerRun) {
        log(`⚠️  Maximale Operationen (${CONFIG.maxOpsPerRun}) erreicht, stoppe.`);
        return;
      }

      // Schritt 4: Aktualisiere Warenkorb nach Entfernungen
      const cart2 = operations > 0 ? await getCart() : cart;

      const hasObBasic2 = hasVariant(cart2, ob.basic);
      const hasObPremium2 = hasVariant(cart2, ob.premium);
      const hasWaBasic2 = hasVariant(cart2, wa.basic);
      const hasWaPremium2 = hasVariant(cart2, wa.premium);

      // Schritt 5: Auto-Add Basics – nur als Fallback wenn Modal abgebrochen wurde
      const modalWasCancelled = sessionStorage.getItem('upsell_modal_cancelled') === '1';

      if (!hasObBasic2 && !hasObPremium2 && modalWasCancelled) {
        log("➕ Modal abgebrochen, kein Onboarding vorhanden → füge Basic Onboarding als Fallback hinzu");
        await addVariant(ob.basic, CONFIG.qty.onboarding);
        operations++;
      } else if (!hasObBasic2 && !hasObPremium2) {
        log("ℹ️  Kein Onboarding vorhanden – warte auf Modal-Auswahl (kein Auto-Add)");
      }

      if (operations >= CONFIG.maxOpsPerRun) {
        log(`⚠️  Maximale Operationen (${CONFIG.maxOpsPerRun}) erreicht, stoppe.`);
        return;
      }

      if (!hasWaBasic2 && !hasWaPremium2 && modalWasCancelled) {
        log("➕ Modal abgebrochen, kein Wartungspaket vorhanden → füge Wartung & Service Basicspaket als Fallback hinzu");
        await addVariant(wa.basic, CONFIG.qty.wartung);
        operations++;
      } else if (!hasWaBasic2 && !hasWaPremium2) {
        log("ℹ️  Kein Wartungspaket vorhanden – warte auf Modal-Auswahl (kein Auto-Add)");
      }

      log(`\n✅ Durchlauf abgeschlossen. Operationen: ${operations}\n`);

      // Schritt 6: Flag löschen nach erfolgreichem Fallback-Add
      if (modalWasCancelled && operations > 0) {
        sessionStorage.removeItem('upsell_modal_cancelled');
        log("🧹 Modal-Cancelled-Flag gelöscht nach erfolgreichem Fallback-Add");
      }

    } catch (error) {
      log("❌ Fehler in run():", error.message);
    } finally {
      isRunning = false;

      // Wenn während der Ausführung neue Änderungen kamen, nochmal laufen
      if (queued) {
        log("🔄 Nochmal ausführen wegen zwischenzeitlicher Änderungen...");
        scheduleRun("queued");
      }
    }
  }

  // ========================================
  // TRIGGER - WANN SOLL LOGIK LAUFEN?
  // ========================================

  function wireUpTriggers() {
    log("📌 Initialisiere Trigger...");

    // Trigger A: Beim Laden der Seite
    scheduleRun("DOMContentLoaded");

    // Trigger B: Abfangen von Add-to-Cart Formularen
    // (Funktioniert bei den meisten Themes)
    document.addEventListener("submit", (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      // Shopify ATC-Formulare haben action mit "/cart/add"
      if (form.action && form.action.includes("/cart/add")) {
        log("📝 ATC-Formular abgesendet, prüfe in 150ms...");
        setTimeout(() => scheduleRun("ATC submit"), 150);
      }
    }, true); // true = capture phase

    // Trigger C: Abfangen von Fetch-Aufrufen zu Cart-Endpoints
    // Eigene Calls (ownCartCall) werden ignoriert um Endlosschleifen zu verhindern
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);

      try {
        if (ownCartCall) return res;  // Eigene Calls ignorieren
        const url = (typeof args[0] === "string") ? args[0] : args[0]?.url;
        if (url && (
          url.includes("/cart/add") ||
          url.includes("/cart/change") ||
          url.includes("/cart/update")
        )) {
          log("🔄 Externer Fetch zu Cart-Endpoint erkannt, prüfe in 150ms...");
          setTimeout(() => scheduleRun("fetch cart mutation"), 150);
        }
      } catch (e) {
        // Fehler beim URL-Parsing ignorieren
      }

      return res;
    };

    // Trigger D: Beobachte Cart-Drawer auf DOM-Änderungen
    // (Theme-abhängig, aber funktioniert bei vielen)
    const cartDrawer = document.querySelector(
      "cart-drawer, #CartDrawer, [data-cart-drawer]"
    );

    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        log("🔍 Cart-Drawer DOM-Änderung erkannt, prüfe in 150ms...");
        scheduleRun("cart drawer mutation");
      });

      observer.observe(cartDrawer, {
        subtree: true,
        childList: true,
        attributes: true,
      });

      log("✓ MutationObserver an Cart-Drawer angehängt");
    } else {
      log("ℹ️  Kein Cart-Drawer gefunden (OK - wird über andere Trigger abgedeckt)");
    }

    log("✓ Alle Trigger aktiv\n");
  }

  // ========================================
  // START
  // ========================================
  wireUpTriggers();
})();
